// Alle Module der App an EINER Stelle.
//
// Seitenleiste, untere Leiste und der Startbildschirm lesen alle aus dieser
// Liste. Ein neues Modul wird hier einmal eingetragen und taucht überall auf —
// sonst driftet die Navigation auseinander und irgendwo fehlt am Ende ein
// Eintrag, den niemand vermisst, bis ihn jemand sucht.

import type { ReactNode } from "react";

export type Rolle = "admin" | "buero" | "dispatch" | "fahrer" | string;

export interface Modul {
  /** Stabiler Schlüssel — steht so in profiles.pinned_modules. NIE ändern. */
  id: string;
  to: string;
  label: string;
  /** Kurze Erklärung in der Seitenleiste. */
  hinweis: string;
  icon: ReactNode;
  /** Farbverlauf für die Kachel auf dem Startbildschirm. */
  von: string;
  bis: string;
  /** Nur für Admins sichtbar. */
  nurAdmin?: boolean;
  /** Gehört auf den Startbildschirm (die vier großen Kacheln). */
  aufStart?: boolean;
  /** Kann an die untere Leiste geheftet werden. */
  anheftbar?: boolean;
}

export const MODULE: Modul[] = [
  {
    id: "news",
    to: "/crm/news",
    label: "Neuigkeiten",
    hinweis: "Was es Neues gibt",
    von: "#3b82f6",
    bis: "#1d4ed8",
    icon: <IconMegafon />,
    aufStart: true,
    anheftbar: true,
  },
  {
    id: "tour",
    to: "/crm/tour",
    label: "Tagestouren",
    hinweis: "Packliste, Route, Übergabe",
    von: "#4a9d5c",
    bis: "#2f6b3d",
    icon: <IconRoute />,
    aufStart: true,
    anheftbar: true,
  },
  {
    id: "qs",
    to: "/crm/qs",
    label: "Aufgaben",
    hinweis: "Zugewiesene Aufgaben abhaken",
    von: "#f59e0b",
    bis: "#b45309",
    icon: <IconPruefliste />,
    aufStart: true,
    anheftbar: true,
  },
  {
    id: "zeit",
    to: "/crm/zeit",
    label: "Stempeluhr",
    hinweis: "Kommen und Gehen",
    von: "#8b5cf6",
    bis: "#6d28d9",
    icon: <IconUhr />,
    aufStart: true,
    anheftbar: true,
  },
  {
    id: "kunden",
    to: "/crm/kunden",
    label: "Kunden",
    hinweis: "Adressen, Körbe, Belege",
    von: "#0ea5e9",
    bis: "#0369a1",
    icon: <IconKunden />,
    anheftbar: true,
  },
  {
    id: "produkte",
    to: "/crm/produkte",
    label: "Produkte",
    hinweis: "Sortiment und Preise",
    von: "#f97316",
    bis: "#c2410c",
    icon: <IconTag />,
    anheftbar: true,
  },
  {
    id: "profil",
    to: "/crm/profil",
    label: "Profil",
    hinweis: "Passwort und Abmelden",
    von: "#64748b",
    bis: "#334155",
    icon: <IconPerson />,
    anheftbar: true,
  },

  // ── Verwaltung
  {
    id: "kasse",
    to: "/crm/admin/kasse",
    label: "Buchhaltung & Kasse",
    hinweis: "Rechnungen, Lieferscheine, Bargeld",
    von: "#14b8a6",
    bis: "#0f766e",
    icon: <IconKasse />,
    nurAdmin: true,
    anheftbar: true,
  },
  {
    id: "bank",
    to: "/crm/admin/bank",
    label: "Bank",
    hinweis: "Geldein- und -ausgänge",
    von: "#6366f1",
    bis: "#4338ca",
    icon: <IconBank />,
    nurAdmin: true,
    anheftbar: true,
  },
  {
    id: "zeiten",
    to: "/crm/admin/zeiten",
    label: "Zeiterfassung",
    hinweis: "Alle Mitarbeiter, alle Schichten",
    von: "#a855f7",
    bis: "#7e22ce",
    icon: <IconUhr />,
    nurAdmin: true,
    anheftbar: true,
  },
  {
    id: "team",
    to: "/crm/admin/team",
    label: "Team",
    hinweis: "Wer arbeitet hier",
    von: "#ec4899",
    bis: "#be185d",
    icon: <IconPersonen />,
    nurAdmin: true,
    anheftbar: true,
  },
];

export const MAX_PINS = 4;

/**
 * Module, die ein Admin einem Mitarbeiter zuteilen kann.
 *
 * Die Verwaltungs-Module (Kasse, Zeiterfassung, Team) sind bewusst NICHT dabei:
 * Die Daten dahinter sind serverseitig auf Admin/Büro beschränkt (RLS). Sie
 * jemandem ins Menü zu legen, würde ihm nur eine Seite zeigen, die dann leer
 * bleibt oder scheitert — ein Haken, der lügt, ist schlimmer als keiner.
 */
export const ZUTEILBARE_MODULE = MODULE.filter((m) => !m.nurAdmin);

/**
 * Was dieser Mensch im Menü sieht.
 *
 * @param rolle  Entwickler zählen als Admin (ihre Rolle IST 'admin').
 * @param erlaubt  NULL = Standard (alles außer Verwaltung).
 *                 Array  = genau diese Module. Profil bleibt immer dabei —
 *                 sonst käme jemand nie an sein eigenes Passwort und könnte
 *                 sich nicht abmelden.
 */
export function moduleFuer(rolle: Rolle | null, erlaubt?: string[] | null): Modul[] {
  const istAdmin = rolle === "admin";

  return MODULE.filter((m) => {
    if (m.nurAdmin) return istAdmin;
    if (istAdmin) return true; // Dem Admin schränkt niemand etwas ein.
    if (!erlaubt) return true; // Kein Eintrag = Standard: alles Nicht-Verwaltung.
    return m.id === "profil" || erlaubt.includes(m.id);
  });
}

export function modulNachId(id: string): Modul | undefined {
  return MODULE.find((m) => m.id === id);
}

// ─────────────────────────────────────────────── Piktogramme
// Einheitlich per Größe skalierbar — die Seitenleiste zeigt sie klein, der
// Startbildschirm groß. Eine Quelle, zwei Größen.

const S = (n: number) => ({
  width: n,
  height: n,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

function IconMegafon() {
  return (
    <svg {...S(24)}>
      <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1Z" />
      <path d="M14 8.5a4 4 0 0 1 0 7" />
      <path d="M17 6a8 8 0 0 1 0 12" />
    </svg>
  );
}

function IconRoute() {
  // Gefaltete Landkarte mit einem Standort-Pin — das vertraute, professionelle
  // Bild für "unterwegs, Ziele abfahren". Ersetzt den alten abstrakten
  // Zwei-Kreise-Zickzack, der eher nach einem Ablaufdiagramm aussah.
  return (
    <svg {...S(24)}>
      <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
      <path d="M9 4v13" />
      <path d="M15 6.5v6" />
      <circle cx="16.5" cy="16" r="2.5" />
      <path d="M16.5 21c1.8-2 3-3.4 3-5a3 3 0 0 0-6 0c0 1.6 1.2 3 3 5Z" />
    </svg>
  );
}

function IconPruefliste() {
  return (
    <svg {...S(24)}>
      <path d="M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z" />
      <path d="M16 5h2a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2" />
      <path d="m8.5 12 2 2 4-4" />
      <path d="M8.5 17.5h7" />
    </svg>
  );
}

function IconUhr() {
  return (
    <svg {...S(24)}>
      <circle cx="12" cy="12.5" r="7.5" />
      <path d="M12 8.5v4l2.5 1.8" />
      <path d="M9 2.5h6" />
    </svg>
  );
}

function IconKunden() {
  return (
    <svg {...S(24)}>
      <path d="M4 20v-1a4 4 0 0 1 4-4h3a4 4 0 0 1 4 4v1" />
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M17 11h4M17 15h4M17 7h4" />
    </svg>
  );
}

function IconPerson() {
  return (
    <svg {...S(24)}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

function IconBank() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 10h18" />
      <path d="M12 3 3 8h18l-9-5Z" />
      <path d="M6 10v7M10 10v7M14 10v7M18 10v7" />
      <path d="M3 20h18" />
    </svg>
  );
}

function IconKasse() {
  return (
    <svg {...S(24)}>
      <path d="M3 8.5h18v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" />
      <path d="M3 8.5 6 4.5h12l3 4" />
      <circle cx="12" cy="14" r="2.3" />
    </svg>
  );
}

function IconTag() {
  return (
    <svg {...S(24)}>
      <path d="M3.5 11.5V4.5a1 1 0 0 1 1-1h7l9 9-8 8-9-9Z" />
      <circle cx="7.5" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconPersonen() {
  return (
    <svg {...S(24)}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19.5a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3.2 3.2 0 0 1 0 5" />
      <path d="M17.5 14a6 6 0 0 1 3.5 5.5" />
    </svg>
  );
}
